import {
  Close as CloseIcon,
  GridView as GridViewIcon,
  ViewList as ViewListIcon,
} from '@mui/icons-material';
import {Box, IconButton} from '@mui/material';
import {FC, useEffect, useMemo, useRef, useState} from 'react';
import {styled} from 'styled-components';

import {NO_IMAGE_PLACEHOLDER} from '@website_frontend/components/image_carousel';
import {isImageLoaded, markImageAsLoaded, preloadImage} from '@website_frontend/lib/image_cache';

interface ImageCarouselFullscreenMobileProps {
  images: string[];
  title?: string;
  onClose: () => void;
}

type LayoutMode = 'list' | 'grid';

export const ImageCarouselFullscreenMobile: FC<ImageCarouselFullscreenMobileProps> = props => {
  const {images, title, onClose} = props;
  const [layoutMode, setLayoutMode] = useState<LayoutMode>('list');
  const [targetImageIndex, setTargetImageIndex] = useState<number | null>(null);
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());
  const containerRef = useRef<HTMLDivElement>(null);
  const imageItemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [viewportSize, setViewportSize] = useState({width: 0, height: 0});

  // Update viewport size
  useEffect(() => {
    const updateSize = () => {
      setViewportSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  // Initialize loaded images from global cache and preload images
  useEffect(() => {
    const initiallyLoaded = new Set<string>();
    images.forEach(src => {
      if (isImageLoaded(src)) {
        initiallyLoaded.add(src);
      } else {
        // Preload images that aren't already loaded
        // Preload first 5 images immediately, rest will load as needed
        const index = images.indexOf(src);
        if (index < 5) {
          preloadImage(src);
        }
      }
    });
    setLoadedImages(initiallyLoaded);
  }, [images]);

  const displayImages = images.length > 0 ? images : [NO_IMAGE_PLACEHOLDER];

  // Calculate optimal grid dimensions
  const gridConfig = useMemo(() => {
    if (layoutMode !== 'grid' || viewportSize.width === 0 || viewportSize.height === 0) {
      return null;
    }

    const imageCount = displayImages.length;
    if (imageCount === 0) return null;

    const gap = 4; // 4px gap between images
    const availableWidth = viewportSize.width;
    const availableHeight = viewportSize.height; // Use full height since button is absolutely positioned

    // Try different column counts to find the best fit
    let bestConfig = {cols: 1, rows: imageCount, cellWidth: 0, cellHeight: 0};
    let bestScore = -Infinity;

    // Try from 1 to imageCount columns
    const maxCols = Math.min(imageCount, 10); // Cap at 10 columns for performance

    for (let cols = 1; cols <= maxCols; cols++) {
      const rows = Math.ceil(imageCount / cols);
      const totalGapWidth = (cols - 1) * gap;
      const totalGapHeight = (rows - 1) * gap;
      const cellWidth = (availableWidth - totalGapWidth) / cols;
      const cellHeight = (availableHeight - totalGapHeight) / rows;

      // Check if this configuration fits
      if (cellWidth > 0 && cellHeight > 0) {
        // Calculate score: prefer 4:3 aspect ratio cells (most images are 4:3)
        const aspectRatio = cellWidth / cellHeight;
        const targetAspectRatio = 4 / 3; // 4:3 aspect ratio
        // Score based on how close the aspect ratio is to 4:3
        const aspectRatioMatch = 1 / (1 + Math.abs(aspectRatio - targetAspectRatio));
        // Also consider space usage efficiency
        const spaceUsage =
          (cellWidth * cellHeight * imageCount) / (availableWidth * availableHeight);
        // Weight aspect ratio match more heavily since images are 4:3
        const score = aspectRatioMatch * 1.5 + spaceUsage * 0.5;

        if (score > bestScore) {
          bestScore = score;
          bestConfig = {cols, rows, cellWidth, cellHeight};
        }
      }
    }

    return bestConfig;
  }, [layoutMode, viewportSize, displayImages.length]);

  const toggleLayout = () => {
    setLayoutMode(prev => (prev === 'list' ? 'grid' : 'list'));
    setTargetImageIndex(null);
  };

  const handleGridImageClick = (index: number) => {
    setTargetImageIndex(index);
    setLayoutMode('list');
  };

  const handleImageLoad = (src: string) => {
    // Image loaded via browser, mark it in cache
    markImageAsLoaded(src);
    setLoadedImages(prev => new Set([...prev, src]));
  };

  // Scroll to target image when switching from grid to list
  useEffect(() => {
    if (layoutMode === 'list' && targetImageIndex !== null && containerRef.current) {
      const imageItem = imageItemRefs.current[targetImageIndex];
      const container = containerRef.current;

      if (imageItem && container) {
        // Wait for DOM to update and images to render
        const scrollToImage = () => {
          // Calculate the cumulative height of all previous items
          let offsetTop = 0;
          for (let i = 0; i < targetImageIndex; i++) {
            const prevItem = imageItemRefs.current[i];
            if (prevItem) {
              offsetTop += prevItem.offsetHeight;
            }
          }

          const containerHeight = container.clientHeight;
          const itemHeight = imageItem.offsetHeight;

          // Center the image vertically in the container
          const targetScrollTop = offsetTop - containerHeight / 2 + itemHeight / 2;

          container.scrollTo({
            top: Math.max(0, targetScrollTop),
            behavior: 'smooth',
          });
        };

        // Use double requestAnimationFrame to ensure layout is complete and images are loaded
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            scrollToImage();
          });
        });
      }

      // Reset target after initiating scroll
      setTargetImageIndex(null);
    }
  }, [layoutMode, targetImageIndex]);

  const iconButtonStyles = {
    p: 1.5,
    backgroundColor: '#ffffff40',
    color: '#00000080',
    backdropFilter: 'blur(2px)',
    '&:hover': {
      backgroundColor: '#ffffffa0',
      color: '#000000',
    },
  };

  return (
    <FullscreenContainer onClick={onClose}>
      <CloseButtonContainer>
        <IconButton
          onClick={e => {
            e.stopPropagation();
            onClose();
          }}
          size="large"
          sx={iconButtonStyles}
          aria-label="Close"
        >
          <CloseIcon fontSize="large" />
        </IconButton>
      </CloseButtonContainer>
      <ToggleButtonContainer>
        <IconButton
          onClick={e => {
            e.stopPropagation();
            toggleLayout();
          }}
          size="large"
          sx={iconButtonStyles}
          aria-label={layoutMode === 'list' ? 'Switch to grid view' : 'Switch to list view'}
        >
          {layoutMode === 'list' ? (
            <GridViewIcon fontSize="large" />
          ) : (
            <ViewListIcon fontSize="large" />
          )}
        </IconButton>
      </ToggleButtonContainer>
      <ScrollContainer
        ref={containerRef}
        onClick={e => e.stopPropagation()}
        $isGrid={layoutMode === 'grid'}
      >
        {layoutMode === 'list' ? (
          <ImageList>
            {displayImages.map((src, index) => (
              <ImageItem
                key={src || index}
                ref={el => {
                  imageItemRefs.current[index] = el;
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    width: '100vw',
                    paddingTop: '75%', // 4:3 aspect ratio
                    overflow: 'hidden',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  }}
                >
                  {!loadedImages.has(src) && <ImagePlaceholder />}
                  <img
                    src={src}
                    alt={title ? `${title} - Image ${index + 1}` : `Image ${index + 1}`}
                    loading={index === 0 ? 'eager' : 'lazy'}
                    onLoad={() => handleImageLoad(src)}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      opacity: loadedImages.has(src) ? 1 : 0,
                      transition: 'opacity 0.3s ease-in-out',
                    }}
                  />
                </Box>
              </ImageItem>
            ))}
          </ImageList>
        ) : (
          gridConfig && (
            <ImageGrid
              $columns={gridConfig.cols}
              $gap={4}
              $cellWidth={gridConfig.cellWidth}
              $cellHeight={gridConfig.cellHeight}
            >
              {displayImages.map((src, index) => (
                <GridImageItem
                  key={src || index}
                  $width={gridConfig.cellWidth}
                  $height={gridConfig.cellHeight}
                  onClick={e => {
                    e.stopPropagation();
                    handleGridImageClick(index);
                  }}
                >
                  {!loadedImages.has(src) && <ImagePlaceholder />}
                  <img
                    src={src}
                    alt={title ? `${title} - Image ${index + 1}` : `Image ${index + 1}`}
                    loading={index < 12 ? 'eager' : 'lazy'}
                    onLoad={() => handleImageLoad(src)}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      cursor: 'pointer',
                      opacity: loadedImages.has(src) ? 1 : 0,
                      transition: 'opacity 0.3s ease-in-out',
                    }}
                  />
                </GridImageItem>
              ))}
            </ImageGrid>
          )
        )}
      </ScrollContainer>
    </FullscreenContainer>
  );
};

ImageCarouselFullscreenMobile.displayName = 'ImageCarouselFullscreenMobile';

const FullscreenContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const CloseButtonContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 10001;
  padding: 16px;
`;

const ToggleButtonContainer = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  z-index: 10001;
  padding: 16px;
`;

const ScrollContainer = styled.div<{$isGrid: boolean}>`
  width: 100%;
  height: 100%;
  overflow-x: hidden;
  overflow-y: ${props => (props.$isGrid ? 'hidden' : 'auto')};
  -webkit-overflow-scrolling: touch;
  scroll-snap-type: ${props => (props.$isGrid ? 'none' : 'y mandatory')};
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  display: flex;
  align-items: ${props => (props.$isGrid ? 'center' : 'flex-start')};
  justify-content: ${props => (props.$isGrid ? 'center' : 'flex-start')};

  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
`;

const ImageList = styled.div`
  display: flex;
  flex-direction: column;
  height: max-content;
  width: 100vw;
`;

const ImageItem = styled.div`
  scroll-snap-align: start;
  width: 100vw;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ImageGrid = styled.div<{
  $columns: number;
  $gap: number;
  $cellWidth: number;
  $cellHeight: number;
}>`
  display: grid;
  grid-template-columns: repeat(${props => props.$columns}, ${props => props.$cellWidth}px);
  grid-auto-rows: ${props => props.$cellHeight}px;
  gap: ${props => props.$gap}px;
  width: fit-content;
  height: fit-content;
  padding: 0;
`;

const GridImageItem = styled.div<{$width: number; $height: number}>`
  position: relative;
  width: ${props => props.$width}px;
  height: ${props => props.$height}px;
  overflow: hidden;
  background-color: rgba(255, 255, 255, 0.05);
`;

const ImagePlaceholder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #d3d3d3;
`;
